import React, { useState } from 'react';

import { Button } from '@repo/videa-components/src/index';
import { useAtom } from 'jotai';
import {
  AlertCircle,
  ExternalLink,
  Info,
  Mic,
  TriangleAlert,
} from 'lucide-react';

import { hasMicrophonePermissionAtom } from '../../atoms';

import { useAudioDeviceManager } from './useAudioDeviceManager';

interface MicrophonePermissionFlowProps {
  onPermissionGranted: () => void;
}

const MicrophonePermissionFlow: React.FC<MicrophonePermissionFlowProps> = ({
  onPermissionGranted,
}) => {
  const [permissionState, setPermissionState] = useState<
    'idle' | 'requesting' | 'granted' | 'denied'
  >('idle');
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [, setHasMicrophonePermission] = useAtom(hasMicrophonePermissionAtom);

  const { refreshDevices } = useAudioDeviceManager();

  const handleRequestPermission = async () => {
    try {
      setPermissionState('requesting');
      setError(null);

      // Request permission with generic audio constraint
      await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      setPermissionState('granted');
      setRetryCount(0); // Reset retry count on success
      setHasMicrophonePermission(true);
      // Refresh devices after permission is granted
      await refreshDevices();
      onPermissionGranted();
    } catch (err) {
      console.error('Permission denied:', err);
      setPermissionState('denied');
      setRetryCount((prev) => prev + 1);
      setError(
        'Microphone access was denied. Please allow access to continue.',
      );
    }
  };

  if (permissionState === 'granted') {
    return null; // Hide this component once permission is granted
  }

  return (
    <div className="vai-flex-col vai-p-4 vai-w-full">
      <div className="vai-flex vai-p-4 vai-w-full vai-bg-blue-50 vai-border vai-border-blue-200 vai-rounded-lg">
        <div className="vai-flex vai-items-start vai-gap-3 vai-w-full ">
          <div className="vai-flex-1">
            <h3 className="vai-text-sm vai-font-bold vai-mb-2">
              Microphone Access Required
            </h3>
            <p className="vai-text-sm vai-mb-4">
              To record audio notes, we need access to your microphone.
            </p>
            {permissionState === 'denied' && (
              <div className="vai-p-3 vai-bg-red-50 vai-border vai-border-red-200 vai-rounded vai-mb-4">
                <div className="vai-flex vai-items-center vai-gap-2">
                  <TriangleAlert className="vai-w-4 vai-h-4 vai-text-red-500" />
                  <p className="vai-text-sm vai-text-red-700">{error}</p>
                </div>
              </div>
            )}

            <div className="vai-flex vai-justify-end">
              <Button
                onClick={handleRequestPermission}
                disabled={permissionState === 'requesting'}
                className="vai-flex vai-justify-end vai-gap-2 vai-font-medium"
              >
                <Mic className="vai-w-4 vai-h-4" />
                {permissionState === 'requesting'
                  ? 'Requesting Access...'
                  : 'Allow Microphone Access'}
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="vai-flex vai-items-start vai-gap-3 vai-w-full vai-mt-4">
        {retryCount >= 3 && (
          <div className="vai-p-3 vai-bg-yellow-50 vai-border vai-border-yellow-200 vai-rounded vai-mb-4 vai-w-full">
            <div className="vai-flex vai-items-start vai-gap-3 vai-w-full ">
              <div className="vai-flex-1">
                <h3 className="vai-text-sm vai-font-bold vai-mb-2">
                  Having trouble?
                </h3>
                <p className="vai-text-sm vai-mb-4">
                  You may need to manually enable microphone access in your
                  browser settings.
                </p>
                <div className="vai-flex vai-justify-end">
                  <a
                    href="https://support.google.com/chrome/answer/2693767"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="vai-inline-flex vai-items-center vai-gap-2 vai-px-3 vai-py-2 vai-text-sm vai-font-medium vai-text-gray-700 vai-bg-white vai-border vai-border-gray-300 vai-rounded-md vai-shadow-sm hover:vai-bg-gray-50 focus:vai-outline-none focus:vai-ring-2 focus:vai-ring-offset-2 focus:vai-ring-blue-500"
                  >
                    <ExternalLink className="vai-w-3 vai-h-3" />
                    Microphone Help Guide
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MicrophonePermissionFlow;
